import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind CSS classes with proper precedence using clsx and tailwind-merge
 * @param inputs - ClassValue array of Tailwind CSS classes
 * @returns Merged className string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Decodes a JWT token without verification to extract its payload.
 * This function only decodes the token and does not verify its signature.
 *
 * @param token - The JWT token string to decode
 * @returns The decoded token payload as DecodedToken object, or null if decoding fails
 */
export function decodeJWT(token: string): DecodedToken | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT token:", error);
    return null;
  }
}

/**
 * Formats a number as a currency string using the specified or default options.
 *
 * @param amount - The numeric value to format as currency.
 * @param options - Optional Intl.NumberFormatOptions to customize formatting.
 *   - currency: The ISO 4217 currency code (default: "BND").
 *   - minimumFractionDigits: Minimum decimal places (default: 2).
 *   - maximumFractionDigits: Maximum decimal places (default: 2).
 *   - currencyDisplay: How to display the currency (default: "narrowSymbol").
 * @returns The formatted currency string.
 */
export function formatCurrency(
  amount: number,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat("en-BN", {
    style: "currency",
    currency: options?.currency ?? "BND",
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    currencyDisplay: options?.currencyDisplay ?? "narrowSymbol",
    ...options,
  }).format(amount);
}

/**
 * Generates a cryptographically secure random alphanumeric string
 * Uses uppercase letters (A-Z), lowercase letters (a-z), and digits (0-9)
 *
 * @param length - The length of the string to generate (default: 62)
 * @returns A random alphanumeric string
 */
export function generateBookedID(length = 62): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  return Array.from(
    crypto.getRandomValues(new Uint8Array(length)),
    (byte) => chars[byte % chars.length]
  ).join("");
}

/**
 * Gets the delivery option ID based on the customer type and fulfilment type.
 *
 * @param customerType - The type of customer.
 * @param fulfilmentType - The type of fulfilment.
 * @returns The delivery option ID.
 */
export function getDeliveryOptionId({
  customerType,
  fulfilmentType,
}: {
  customerType: CustomerType;
  fulfilmentType: FulfilmentType;
}) {
  if (
    customerType === "RES" ||
    customerType === "MOBI-PREPAID" ||
    customerType === "TOURIST"
  ) {
    if (fulfilmentType === "Instant Delivery") {
      return 1;
    }

    if (fulfilmentType === "Delivery") {
      return 3;
    }

    if (fulfilmentType === "Pick Up") {
      return 2;
    }
  }

  if (customerType === "MOBI-POSTPAID" || customerType === "FREEDOM") {
    if (fulfilmentType === "Instant Delivery") {
      return 3;
    }

    if (fulfilmentType === "Delivery") {
      return 2;
    }

    if (fulfilmentType === "Pick Up") {
      return 1;
    }
  }
  return;
}

const NRIC_NUMBER_REGEX = /^\d{8}$/;

/**
 * Determines the Brunei NRIC IC colour from the NRIC number.
 * Input must be exactly 8 digits (no dashes). Returns "passport" if the value
 * does not match any known NRIC block/serial patterns.
 *
 * Rules
 * - Below year 2000 (legacy blocks):
 *   - Yellow: 00-000001–00-080000, 00-110001–00-130000, 00-250001–00-650000
 *   - Purple: 30-080001–30-110000, 30-200001–30-250000
 *   - Green:  50-130001–50-200000, 50-650001–50-999999
 * - Year 2000 and above:
 *   - Yellow: block 01–29
 *   - Purple: block 31–49
 *   - Green:  block 51–99
 *
 * @param nricNumber - 8-digit NRIC number without dashes.
 * @returns "yellow" | "purple" | "green" | "passport"
 */
export function getICColour(nricNumber: string) {
  // Must be exactly 8 digits (no dashes)
  if (!NRIC_NUMBER_REGEX.test(nricNumber)) {
    return "passport";
  }

  const block = Number(nricNumber.slice(0, 2));
  const serial = Number(nricNumber.slice(2));
  const inRange = (x: number, min: number, max: number) => x >= min && x <= max;

  // Below year 2000 (special legacy blocks)
  if (block === 0) {
    if (
      inRange(serial, 1, 80_000) ||
      inRange(serial, 110_001, 130_000) ||
      inRange(serial, 250_001, 650_000)
    ) {
      return "yellow";
    }
    return "passport";
  }

  if (block === 30) {
    if (inRange(serial, 80_001, 110_000) || inRange(serial, 200_001, 250_000)) {
      return "purple";
    }
    return "passport";
  }

  if (block === 50) {
    if (
      inRange(serial, 130_001, 200_000) ||
      inRange(serial, 650_001, 999_999)
    ) {
      return "green";
    }
    return "passport";
  }

  // Year 2000 and above
  if (block >= 1 && block <= 29) {
    return "yellow";
  }
  if (block >= 31 && block <= 49) {
    return "purple";
  }
  if (block >= 51 && block <= 99) {
    return "green";
  }

  return "passport";
}

/**
 * Sanitizes an alphanumeric string by removing all non-alphanumeric characters.
 *
 * @param value - The string to sanitize.
 * @returns The sanitized string.
 */
export function sanitizeAlphanumeric(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, "");
}
